import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ToastComponent } from "../../../../shared/components/toast/toast.component";
import { AdminTopbarComponent } from "../../components/admin-topbar/admin-topbar.component";
import { AdminNavigationComponent, AdminSectionId } from "../../components/admin-navigation/admin-navigation.component";
import { PromotionCodeListItem } from '../../../../core/interfaces/payment-interfaces';
import { PaymentService } from '../../../../core/api/payment.service';

@Component({
  selector: 'app-admin-promotion-codes-page',
  standalone: true,
  imports: [CommonModule, TranslocoPipe, ToastComponent, AdminTopbarComponent, AdminNavigationComponent],
  templateUrl: './admin-promotion-codes-page.component.html',
  styleUrl: './admin-promotion-codes-page.component.css'
})
export class AdminPromotionCodesPageComponent {
  AdminSectionId = AdminSectionId;

  promotionCodesList: PromotionCodeListItem[] = [];
  isLoading = false;

  @ViewChild(ToastComponent) toast!: ToastComponent;

  constructor(private paymentService: PaymentService, private translocoService: TranslocoService) { }

  ngOnInit(): void {
    this.loadPromotionCodes();
  }

  loadPromotionCodes(): void {
    this.isLoading = true;

    this.paymentService.getPromotionCodesList().subscribe({
      next: (codes) => {
        this.promotionCodesList = codes ?? [];
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.toast?.showToast('shared.error', 'admin.promotion_codes_load_error', 'error', 3000, true);
      }
    });
  }

  getCampaignName(item: PromotionCodeListItem): string {
    if (item.coupon?.name) {
      return item.coupon.name;
    }

    if (item.metadata && item.metadata['campaign_name']) {
      return String(item.metadata['campaign_name']);
    }

    return item.code;
  }

  getDiscountDescription(item: PromotionCodeListItem): string {
    const coupon = item.coupon;
    if (!coupon) {
      return '';
    }

    // Discount
    let discountPart = '';

    if (coupon.percent_off != null) {
      discountPart = this.translocoService.translate('settings.promotion.percent_off', {
        percent: coupon.percent_off
      });
    } else if (coupon.amount_off != null && coupon.currency) {
      discountPart = this.translocoService.translate('settings.promotion.amount_off', {
        amount: coupon.amount_off,
        currency: coupon.currency.toUpperCase()
      });
    } else {
      discountPart = this.translocoService.translate('settings.promotion.generic_discount');
    }

    // Duration
    let durationPart = '';

    if (coupon.duration === 'once') {
      durationPart = this.translocoService.translate('settings.promotion.duration_once');
    } else if (coupon.duration === 'repeating' && coupon.duration_in_months) {
      durationPart = this.translocoService.translate('settings.promotion.duration_repeating', {
        months: coupon.duration_in_months
      });
    } else if (coupon.duration === 'forever') {
      durationPart = this.translocoService.translate('settings.promotion.duration_forever');
    }

    // Expiration - from promotion code or coupon
    let expirationPart = '';
    const expiresAt = item.expires_at || coupon.redeem_by;
    if (expiresAt) {
      const date = new Date(expiresAt).toLocaleDateString();
      expirationPart = this.translocoService.translate('settings.promotion.valid_until', { date });
    }

    return [discountPart, durationPart, expirationPart].filter(Boolean).join(' ');
  }

  // Available / Max (3/20)
  getRedemptionInfo(item: PromotionCodeListItem): string {
    const max = item.max_redemptions;
    const redeemed = item.times_redeemed ?? 0;

    if (max != null) {
      return `${redeemed}/${max}`;
    }

    // If no max configured, just show redeemed
    return `${redeemed}`;
  }

  generateUrl(promotion: PromotionCodeListItem): string {
    const pageURL = window.location.origin;
    return `${pageURL}/auth/signup?promotion=${encodeURIComponent(promotion.code)}`;
  }

  copyText(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.toast?.showToast('shared.success', 'admin.copied_to_clipboard', 'success', 2000, true);
    });
  }

}
